import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { addDoc, collection, deleteDoc, doc, getDocs, query, setDoc, updateDoc, where } from 'firebase/firestore';
import db from '../../firebaseConfig';
import usePageTitle from '../../hooks/usePageTitle';
import { adminCallable } from '../../utils/adminAuth';
import {
  buildServiceQuestionFlow,
  calculateServiceResponseQuota,
  DEFAULT_SERVICE_RESPONSE_POLICY,
  moveServiceQuestion,
  normalizeServiceResponsePolicy,
} from '../../utils/serviceQuestionnaire';
import { callJamboProxy, useTianlanStore, withShopScope } from '../../utils/tianlanStore';
import {
  isCombinedProduct,
  normalizeCombinedFormSettings,
} from '../../utils/serviceCombinedProducts';
import JamboCommodityPicker from './JamboCommodityPicker';

const commodityIdOf = (item) => String(item?.commodityId || item?.id || item?.jamboCommodityId || '');
const productImageOf = (item = {}) => String(
  item.image || item.photo || item.imageUrl || item.image_url || item.coverImage || '',
).trim();
const normalizeStyleKey = (value) => String(value || '').trim().replace(/\s+/g, '').replace(/[／/\\-]/g, '').toLowerCase() || 'noStyle';
const legacyStyleKey = (value) => String(value || '').trim().replace(/\s+/g, '').toLowerCase() || 'noStyle';
const makeLocalId = () =>
  (window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `q_${Date.now()}_${Math.random().toString(16).slice(2)}`);
const questionDefaults = (type) => ({
  id: makeLocalId(),
  type,
  label:
    type === 'date'
      ? '請選擇日期'
      : type === 'calendar_date'
        ? '請選擇國曆／農曆生日'
        : type === 'birth_time'
          ? '請選擇出生時辰'
      : type === 'choice'
        ? '請選擇項目'
        : '請輸入文字',
  required: true,
  minLength: type === 'text' ? 0 : '',
  maxLength: type === 'text' ? 200 : '',
  dateMode: type === 'date' ? 'date' : '',
  options: type === 'choice' ? ['是', '否'] : [],
  branching: false,
  condition: null,
});

const branchingQuestionDefaults = () => ({
  ...questionDefaults('choice'),
  label: '請選擇要提供的資料',
  options: ['車牌', '姓名'],
  branching: true,
});

const questionTypeLabel = (question = {}) => {
  if (question.type === 'text') return '文字';
  if (question.type === 'date') return '年月日';
  if (question.type === 'calendar_date') return '國曆／農曆';
  if (question.type === 'birth_time') return '時辰';
  if (question.type === 'choice' && question.branching) return '分流選擇';
  return '選項';
};

const suggestedTemplateDescription = (title = '', questions = []) => {
  const branchingQuestion = questions.find((question) => (
    question.type === 'choice' && question.branching === true && (question.options || []).length >= 2
  ));
  const options = (branchingQuestion?.options || []).map((option) => String(option || '').trim()).filter(Boolean);
  if (options.length >= 2) {
    return `請選擇提供「${options[0]}」或「${options[1]}」，系統只會顯示您所選項目需要填寫的內容。`;
  }
  const name = String(title || '').trim();
  return name
    ? `請依序填寫「${name}」所需資料，我們會依內容為您處理後續服務。`
    : '請依序填寫以下資料，我們會依內容為您處理後續服務。';
};

const responsePolicyPreviewQuantities = (policy = {}) => {
  const normalized = normalizeServiceResponsePolicy(policy);
  if (normalized.mode !== 'minimum_then_per_n') return [1, 2, 4, 10];
  return [...new Set([
    Math.max(1, normalized.minimumQuantity - 1),
    normalized.minimumQuantity,
    normalized.minimumQuantity + 1,
    normalized.minimumQuantity + 2,
  ])];
};

const formPolicyTargetsOf = (products = [], fallbackPolicy = DEFAULT_SERVICE_RESPONSE_POLICY) => products.flatMap((product) => {
  const parentId = commodityIdOf(product);
  const settings = normalizeCombinedFormSettings(product);
  const parentPolicy = normalizeServiceResponsePolicy(
    product.formResponsePolicy || fallbackPolicy,
  );
  if (!settings.combined || product.formProductMode !== 'children') {
    return [{
      key: `product:${parentId}`,
      parentId,
      childKey: '',
      product: { ...product, policyEditorKey: `product:${parentId}` },
      policy: parentPolicy,
      kindLabel: settings.combined ? '整組商品' : '任務商品',
      contextLabel: '同一張 PA、同一商品與同一樣式會合併計算。',
    }];
  }

  const selectedKeys = new Set(
    Array.isArray(product.selectedFormChildKeys)
      ? product.selectedFormChildKeys
      : settings.selectedFormChildKeys,
  );
  const childPolicies = settings.formChildResponsePolicies || {};
  const parentLabel = [product.partNumber || parentId, product.name].filter(Boolean).join('　');
  return settings.subCommodities
    .filter((child) => selectedKeys.has(child.key))
    .map((child) => ({
      key: `${parentId}::${child.key}`,
      parentId,
      childKey: child.key,
      product: { ...child, policyEditorKey: `${parentId}::${child.key}` },
      policy: normalizeServiceResponsePolicy(
        childPolicies[child.key] || child.formResponsePolicy || parentPolicy,
      ),
      kindLabel: '拆分子商品',
      contextLabel: `來自 ${parentLabel || '組合商品'}・每組含 ${child.quantity} 件；依這個子商品的總數獨立計算。`,
    }));
});

function ProductThumbnail({ product = {}, size = 'medium' }) {
  const src = productImageOf(product);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const label = product.partNumber || product.itemNumber || product.name || commodityIdOf(product) || '商品';
  return (
    <span className={`tl-product-thumbnail is-${size}`} aria-label={`${label} 商品圖片`}>
      {src && !failed
        ? <img src={src} alt={label} onError={() => setFailed(true)} />
        : <span>無圖</span>}
    </span>
  );
}

function ProductTile({ product = {}, meta = '' }) {
  const id = commodityIdOf(product);
  return (
    <div className="tl-rule-product-tile">
      <ProductThumbnail product={product} size="rule" />
      <span>
        <strong>{product.partNumber || product.itemNumber || id || '未設定料號'}</strong>
        <small>{product.name || '未命名商品'}</small>
        {meta && <em>{meta}</em>}
      </span>
    </div>
  );
}

function PolicyQuantityControl({ label, value, onChange, showPresets = false }) {
  const quantity = Math.max(1, Math.floor(Number(value) || 1));
  const update = (nextValue) => onChange(Math.max(1, Math.min(999, Math.floor(Number(nextValue) || 1))));

  return (
    <div className="tl-labeled-field tl-policy-number-field">
      <span>{label}</span>
      <div className="tl-policy-stepper">
        <button
          type="button"
          aria-label={`${label}減少 1`}
          disabled={quantity <= 1}
          onClick={() => update(quantity - 1)}
        >
          −
        </button>
        <div>
          <input
            className="modern-input"
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            min="1"
            max="999"
            step="1"
            aria-label={label}
            value={quantity}
            onFocus={(event) => event.currentTarget.select()}
            onWheel={(event) => event.currentTarget.blur()}
            onChange={(event) => update(event.target.value)}
          />
          <em>件</em>
        </div>
        <button
          type="button"
          aria-label={`${label}增加 1`}
          disabled={quantity >= 999}
          onClick={() => update(quantity + 1)}
        >
          ＋
        </button>
      </div>
      {showPresets && (
        <div className="tl-policy-quick-values" aria-label={`${label}常用數量`}>
          {[1, 2, 3, 5, 10].map((preset) => (
            <button
              type="button"
              key={preset}
              className={quantity === preset ? 'is-selected' : ''}
              onClick={() => update(preset)}
            >
              {preset} 件
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductResponsePolicyEditor({ product, policy: rawPolicy, onChange, kindLabel = '任務商品', contextLabel = '' }) {
  const policy = normalizeServiceResponsePolicy(rawPolicy);
  const productId = product.policyEditorKey || commodityIdOf(product);
  const productLabel = [product.partNumber || product.itemNumber || productId, product.name]
    .filter(Boolean)
    .join('　');
  const updatePolicy = (patch) => onChange(normalizeServiceResponsePolicy({ ...policy, ...patch }));

  return (
    <div className="tl-response-policy-panel tl-product-response-policy">
      <div className="tl-product-policy-heading">
        <ProductThumbnail product={product} size="policy" />
        <div>
          <span>{kindLabel}</span>
          <strong>{productLabel || '未命名商品'}</strong>
          <small>{contextLabel || '同一張 PA、同一商品與同一樣式會合併計算。'}</small>
        </div>
      </div>
      <div className="tl-question-condition-head">
        <strong>需要填幾份？</strong>
      </div>
      <div className="tl-response-policy-options">
        {[
          { mode: 'per_unit', title: '每件一份', description: '買 4 件，可填 4 份' },
          { mode: 'per_n', title: '每 N 件一份', description: '例如每 2 件可填 1 份' },
          { mode: 'minimum_once', title: '滿 N 件填一份', description: '數量達標才出現，最多 1 份' },
          { mode: 'minimum_then_per_n', title: '滿 N 件起，每件一份', description: '未滿不顯示；達門檻後，買幾件就填幾份' },
        ].map((option) => (
          <label
            key={option.mode}
            className={`tl-response-policy-card ${policy.mode === option.mode ? 'is-selected' : ''}`}
          >
            <input
              type="radio"
              name={`formResponsePolicy-${productId}`}
              value={option.mode}
              checked={policy.mode === option.mode}
              onChange={() => updatePolicy({
                mode: option.mode,
                ...(option.mode === 'minimum_then_per_n' && policy.mode !== 'minimum_then_per_n'
                  ? {
                    minimumQuantity: policy.minimumQuantity > 1 ? policy.minimumQuantity : 2,
                  }
                  : {}),
              })}
            />
            <span><strong>{option.title}</strong><small>{option.description}</small></span>
          </label>
        ))}
      </div>
      {policy.mode !== 'per_unit' && policy.mode !== 'minimum_then_per_n' && (
        <PolicyQuantityControl
          label={policy.mode === 'minimum_once' ? '滿幾件才出現？' : '每幾件可填一份？'}
          value={policy.quantityPerResponse}
          showPresets
          onChange={(quantity) => updatePolicy({ quantityPerResponse: quantity })}
        />
      )}
      {policy.mode === 'minimum_then_per_n' && (
        <div className="tl-policy-fields-grid is-single-field">
          <PolicyQuantityControl
            label="滿幾件開始填？"
            value={policy.minimumQuantity}
            showPresets
            onChange={(quantity) => updatePolicy({ minimumQuantity: quantity })}
          />
        </div>
      )}
      <div className="tl-policy-preview">
        <span>即時計算</span>
        <div className="tl-policy-preview-grid">
          {responsePolicyPreviewQuantities(policy).map((quantity) => (
            <em key={quantity}>
              買 {quantity} 件 <strong>{calculateServiceResponseQuota(quantity, policy)}</strong> 份
            </em>
          ))}
        </div>
      </div>
    </div>
  );
}

function ServiceQuestionFlow({ questions = [], compact = false }) {
  const flow = buildServiceQuestionFlow(questions);
  const visibleFlow = compact ? flow.slice(0, 8) : flow;

  return (
    <div className={`tl-question-flow ${compact ? 'is-compact' : ''}`}>
      {visibleFlow.map((item) => (
        <div
          className={`tl-question-flow-row ${item.hasCondition ? 'is-branch' : ''} ${item.isConditionValid ? '' : 'is-invalid'}`}
          key={item.id}
        >
          <span className="tl-question-flow-number">{item.number}</span>
          <span className="tl-question-flow-copy">
            <small>
              {item.hasCondition
                ? (item.isConditionValid
                  ? `選「${item.conditionValues.join('、') || '尚未指定'}」時`
                  : '顯示條件需要修正')
                : (item.index === 0 ? '先詢問' : '接著詢問')}
            </small>
            <strong>{item.label}</strong>
            {!compact && (
              <em>{questionTypeLabel(item)}{item.required ? '・必填' : '・選填'}</em>
            )}
          </span>
        </div>
      ))}
      {flow.length > visibleFlow.length && (
        <div className="tl-question-flow-more">還有 {flow.length - visibleFlow.length} 題</div>
      )}
      {flow.length === 0 && <div className="tl-question-flow-empty">尚未新增問題</div>}
    </div>
  );
}
const stockSettingByStyle = (collection = {}, label = '') => {
  const normalizedKey = normalizeStyleKey(label);
  const oldKey = legacyStyleKey(label);
  if (collection[normalizedKey]) return collection[normalizedKey];
  if (collection[oldKey]) return collection[oldKey];
  const matchedKey = Object.keys(collection).find((key) => normalizeStyleKey(key) === normalizedKey || legacyStyleKey(key) === oldKey);
  return matchedKey ? collection[matchedKey] : null;
};
const styleLabelsOf = (item) => {
  const fromOptions = Array.isArray(item?.styleOptions)
    ? item.styleOptions.map((style) => style.label || [style.keyword, style.subKeyword].filter(Boolean).join(' / '))
    : [];
  const fromStyles = Array.isArray(item?.styles) ? item.styles : [];
  return [...new Set([...fromOptions, ...fromStyles].map((style) => String(style || '').trim()).filter(Boolean))];
};
const manualStockOf = (item, label) => {
  const stock = stockSettingByStyle(item?.manualStockByStyle, label) || stockSettingByStyle(item?.stockSettings, label) || { mode: 'limited', quantity: '', reservedQuantity: 0 };
  return {
    ...stock,
    mode: stock.mode || stock.manualStockMode || 'limited',
    quantity: stock.quantity ?? stock.manualStockQuantity ?? '',
    reservedQuantity: Number(stock.reservedQuantity || 0) || 0,
  };
};
const manualStockText = (item, label) => {
  const stock = manualStockOf(item, label);
  if (stock.mode === 'unlimited') return '手動可選：無上限';
  const quantity = Number(stock.quantity || 0) || 0;
  const reserved = Number(stock.reservedQuantity || 0) || 0;
  return `可選 ${quantity} / 已保留 ${reserved} / 剩餘 ${Math.max(quantity - reserved, 0)}`;
};

function compactCommodity(item) {
  const styles = styleLabelsOf(item);
  const combinedSettings = normalizeCombinedFormSettings(item);
  const formChildResponsePolicies = Object.fromEntries(
    Object.entries(combinedSettings.formChildResponsePolicies || {}).map(([key, policy]) => [
      key,
      normalizeServiceResponsePolicy(policy || DEFAULT_SERVICE_RESPONSE_POLICY),
    ]),
  );
  return {
    commodityId: commodityIdOf(item),
    id: commodityIdOf(item),
    partNumber: item.partNumber || '',
    name: item.name || '',
    image: item.image || '',
    price: item.price || 0,
    refundPriceDifference: item.refundPriceDifference === true,
    styles,
    selectedStyles: Array.isArray(item.selectedStyles) ? item.selectedStyles : styles,
    styleOptions: item.styleOptions || [],
    formResponsePolicy: normalizeServiceResponsePolicy(item.formResponsePolicy || DEFAULT_SERVICE_RESPONSE_POLICY),
    ...combinedSettings,
    formChildResponsePolicies,
  };
}

const emptyStockSetting = () => ({
  mode: 'limited',
  quantity: 0,
  reservedQuantity: 0,
  manualStockMode: 'limited',
  manualStockQuantity: 0,
  availableQuantity: 0,
});

const normalizeStockSetting = (setting) => {
  const source = setting || emptyStockSetting();
  const mode = source.mode === 'unlimited' || source.manualStockMode === 'unlimited' ? 'unlimited' : 'limited';
  const quantity = Number(source.quantity ?? source.manualStockQuantity ?? 0) || 0;
  const reservedQuantity = Number(source.reservedQuantity || 0) || 0;
  return {
    ...source,
    mode,
    quantity,
    reservedQuantity,
    manualStockMode: mode,
    manualStockQuantity: quantity,
    availableQuantity: mode === 'unlimited' ? null : Math.max(quantity - reservedQuantity, 0),
  };
};

function mergeCommodityFromJambo(currentProduct, jamboProduct, options = {}) {
  const isTarget = options.isTarget === true;
  const currentStyles = styleLabelsOf(currentProduct);
  const nextBase = compactCommodity({
    ...jamboProduct,
    commodityId: commodityIdOf(jamboProduct) || commodityIdOf(currentProduct),
    id: commodityIdOf(jamboProduct) || commodityIdOf(currentProduct),
  });
  const nextStyles = styleLabelsOf(nextBase);
  const currentSelected = Array.isArray(currentProduct.selectedStyles)
    ? currentProduct.selectedStyles
    : currentStyles;
  const selectedKeys = new Set(currentSelected.map(normalizeStyleKey));
  const allExistingSelected =
    currentStyles.length === 0 ||
    currentStyles.every((style) => selectedKeys.has(normalizeStyleKey(style)));
  const nextSelectedStyles = nextStyles.length === 0
    ? []
    : allExistingSelected
      ? nextStyles
      : nextStyles.filter((style) => selectedKeys.has(normalizeStyleKey(style)));
  const merged = {
    ...currentProduct,
    ...nextBase,
    selectedStyles: nextSelectedStyles,
    formProductMode: currentProduct.formProductMode === 'children' ? 'children' : nextBase.formProductMode,
    selectedFormChildKeys: (() => {
      const nextKeys = new Set((nextBase.subCommodities || []).map((child) => child.key));
      return Array.isArray(currentProduct.selectedFormChildKeys)
        ? currentProduct.selectedFormChildKeys.filter((key) => nextKeys.has(key))
        : nextBase.selectedFormChildKeys;
    })(),
    formResponsePolicy: normalizeServiceResponsePolicy(
      currentProduct.formResponsePolicy || nextBase.formResponsePolicy || DEFAULT_SERVICE_RESPONSE_POLICY
    ),
    formChildResponsePolicies: (() => {
      const nextKeys = new Set((nextBase.subCommodities || []).map((child) => child.key));
      return Object.fromEntries(
        Object.entries(currentProduct.formChildResponsePolicies || {})
          .filter(([key]) => nextKeys.has(key))
          .map(([key, policy]) => [key, normalizeServiceResponsePolicy(policy)]),
      );
    })(),
    refreshedAt: new Date().toISOString(),
  };

  if (!isTarget) return merged;

  const currentStock = currentProduct.manualStockByStyle || {};
  const currentSettings = currentProduct.stockSettings || {};
  const nextStock = {};
  const nextSettings = {};
  (nextStyles.length > 0 ? nextStyles : ['']).forEach((style) => {
    const styleKey = normalizeStyleKey(style);
    const existing = stockSettingByStyle(currentStock, style) || stockSettingByStyle(currentSettings, style);
    const normalized = normalizeStockSetting(existing || emptyStockSetting());
    nextStock[styleKey] = normalized;
    nextSettings[styleKey] = normalized;
  });
  return {
    ...merged,
    refundPriceDifference: currentProduct.refundPriceDifference === true,
    manualStockByStyle: nextStock,
    stockSettings: nextSettings,
  };
}

function TianlanJamboRuleManager() {
  usePageTitle('商品規則管理', '天藍小舖後台', '建立商品換貨與售後表單規則');
  const currentShop = useTianlanStore();
  const [rules, setRules] = useState([]);
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [sourceProducts, setSourceProducts] = useState([]);
  const [targetProducts, setTargetProducts] = useState([]);
  const [allowCancelRefund, setAllowCancelRefund] = useState(false);
  const [ruleTaskType, setRuleTaskType] = useState('exchange');
  const [enableFormTask, setEnableFormTask] = useState(false);
  const [selectedFormTemplateId, setSelectedFormTemplateId] = useState('');
  const [formTemplateTitle, setFormTemplateTitle] = useState('');
  const [formTemplateDescription, setFormTemplateDescription] = useState('');
  const [formAllowSameAsPrevious, setFormAllowSameAsPrevious] = useState(true);
  const [formAllowQuestionCollapse, setFormAllowQuestionCollapse] = useState(true);
  const [formAllowAnswerGroupCountAdjust, setFormAllowAnswerGroupCountAdjust] = useState(true);
  const [formQuestions, setFormQuestions] = useState([
    { ...questionDefaults('text'), label: '請輸入姓名', minLength: 1, maxLength: 30 },
    { ...questionDefaults('calendar_date'), label: '請選擇生日' },
  ]);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState('');
  const [templateTitle, setTemplateTitle] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateAllowSameAsPrevious, setTemplateAllowSameAsPrevious] = useState(true);
  const [templateAllowQuestionCollapse, setTemplateAllowQuestionCollapse] = useState(true);
  const [templateAllowAnswerGroupCountAdjust, setTemplateAllowAnswerGroupCountAdjust] = useState(true);
  const [templateQuestions, setTemplateQuestions] = useState([
    { ...questionDefaults('text'), label: '請輸入姓名', minLength: 1, maxLength: 30 },
  ]);
  const [draggedQuestionId, setDraggedQuestionId] = useState('');
  const [dragOverQuestionId, setDragOverQuestionId] = useState('');
  const [questionOrderNotice, setQuestionOrderNotice] = useState('');
  const [editingRuleId, setEditingRuleId] = useState('');
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadRules = async () => {
    setIsLoading(true);
    try {
      const [snap, templateSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'tianlan_exchange_rules'),
          where('shopUserId', '==', currentShop.shopUserId)
        )),
        getDocs(query(
          collection(db, 'tianlan_service_question_templates'),
          where('shopUserId', '==', currentShop.shopUserId)
        )),
      ]);
      setRules(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
      setServiceTemplates(
        templateSnap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => String(b.updatedAt || b.createdAt || '').localeCompare(String(a.updatedAt || a.createdAt || '')))
      );
    } catch (error) {
      alert(`載入商品規則失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShop.shopUserId]);

  const pickedIds = useMemo(() => [
    ...sourceProducts.map(commodityIdOf),
    ...targetProducts.map(commodityIdOf),
  ], [sourceProducts, targetProducts]);
  const formPolicyTargets = useMemo(
    () => formPolicyTargetsOf(sourceProducts),
    [sourceProducts],
  );

  const addUnique = (list, item) => {
    const id = commodityIdOf(item);
    if (!id || list.some((existing) => commodityIdOf(existing) === id)) return list;
    return [...list, compactCommodity(item)];
  };

  const toggleStyle = (list, setter, productId, style) => {
    setter(list.map((product) => {
      if (commodityIdOf(product) !== productId) return product;
      const current = Array.isArray(product.selectedStyles) ? product.selectedStyles : styleLabelsOf(product);
      const next = current.includes(style)
        ? current.filter((item) => item !== style)
        : [...current, style];
      return { ...product, selectedStyles: next };
    }));
  };

  const setFormProductMode = (list, setter, productId, mode) => {
    setter(list.map((product) => {
      if (commodityIdOf(product) !== productId) return product;
      const subCommodities = normalizeCombinedFormSettings(product).subCommodities;
      return {
        ...product,
        formProductMode: mode === 'children' ? 'children' : 'combined',
        selectedFormChildKeys: Array.isArray(product.selectedFormChildKeys) && product.selectedFormChildKeys.length > 0
          ? product.selectedFormChildKeys
          : subCommodities.map((child) => child.key),
      };
    }));
  };

  const toggleFormChild = (list, setter, productId, childKey) => {
    setter(list.map((product) => {
      if (commodityIdOf(product) !== productId) return product;
      const current = Array.isArray(product.selectedFormChildKeys) ? product.selectedFormChildKeys : [];
      return {
        ...product,
        selectedFormChildKeys: current.includes(childKey)
          ? current.filter((key) => key !== childKey)
          : [...current, childKey],
      };
    }));
  };

  const updateProductResponsePolicy = (productId, nextPolicy, childKey = '') => {
    setSourceProducts((products) => products.map((product) => (
      commodityIdOf(product) === productId
        ? childKey
          ? {
            ...product,
            formChildResponsePolicies: {
              ...(product.formChildResponsePolicies || {}),
              [childKey]: normalizeServiceResponsePolicy(nextPolicy),
            },
          }
          : { ...product, formResponsePolicy: normalizeServiceResponsePolicy(nextPolicy) }
        : product
    )));
  };

  const removeSelectedProduct = (list, setter, productId) => {
    setter(list.filter((product) => commodityIdOf(product) !== productId));
  };

  const updateManualStock = (list, setter, productId, style, patch) => {
    const styleKey = normalizeStyleKey(style);
    setter(list.map((product) => {
      if (commodityIdOf(product) !== productId) return product;
      const currentStock = product.manualStockByStyle || {};
      const currentSettings = product.stockSettings || {};
      const current = currentStock[styleKey] || currentSettings[styleKey] || { mode: 'limited', quantity: '', reservedQuantity: 0 };
      const next = {
        ...current,
        ...patch,
      };
      const mode = next.mode === 'unlimited' ? 'unlimited' : 'limited';
      const quantity = Number(next.quantity || next.manualStockQuantity || 0) || 0;
      const reservedQuantity = Number(next.reservedQuantity || 0) || 0;
      const normalized = {
        ...next,
        mode,
        quantity: next.quantity ?? '',
        reservedQuantity,
        manualStockMode: mode,
        manualStockQuantity: quantity,
        availableQuantity: mode === 'unlimited' ? null : Math.max(quantity - reservedQuantity, 0),
      };
      return {
        ...product,
        manualStockByStyle: {
          ...currentStock,
          [styleKey]: normalized,
        },
        stockSettings: {
          ...currentSettings,
          [styleKey]: normalized,
        },
      };
    }));
  };

  const updateAllManualStockForProduct = (list, setter, productId, mode) => {
    setter(list.map((product) => {
      if (commodityIdOf(product) !== productId) return product;
      const styles = styleLabelsOf(product);
      const currentStock = product.manualStockByStyle || {};
      const currentSettings = product.stockSettings || {};
      const nextStock = { ...currentStock };
      const nextSettings = { ...currentSettings };
      (styles.length > 0 ? styles : ['']).forEach((style) => {
        const styleKey = normalizeStyleKey(style);
        const current = currentStock[styleKey] || currentSettings[styleKey] || { mode: 'limited', quantity: 0, reservedQuantity: 0 };
        const quantity = Number(current.quantity ?? current.manualStockQuantity ?? 0) || 0;
        const reservedQuantity = Number(current.reservedQuantity || 0) || 0;
        const normalized = {
          ...current,
          mode,
          quantity,
          reservedQuantity,
          manualStockMode: mode,
          manualStockQuantity: quantity,
          availableQuantity: mode === 'unlimited' ? null : Math.max(quantity - reservedQuantity, 0),
        };
        nextStock[styleKey] = normalized;
        nextSettings[styleKey] = normalized;
      });
      return {
        ...product,
        manualStockByStyle: nextStock,
        stockSettings: nextSettings,
      };
    }));
  };

  const refreshProductListFromJambo = async (items, options = {}) => {
    const ids = items.map(commodityIdOf).filter(Boolean);
    if (ids.length === 0) return items;
    const result = await callJamboProxy('loadCommodityByIdBatch', { commodityIds: ids }, currentShop);
    const byId = new Map((result.commodities || []).map((commodity) => [commodityIdOf(commodity), commodity]));
    return items.map((product) => {
      const latest = byId.get(commodityIdOf(product));
      return latest ? mergeCommodityFromJambo(product, latest, options) : product;
    });
  };

  const refreshFormProductsFromJambo = async () => {
    if (sourceProducts.length === 0 && targetProducts.length === 0) {
      alert('請先選擇缺貨商品或可換商品');
      return;
    }
    setIsLoading(true);
    try {
      const [nextSources, nextTargets] = await Promise.all([
        refreshProductListFromJambo(sourceProducts, { isTarget: false }),
        refreshProductListFromJambo(targetProducts, { isTarget: true }),
      ]);
      setSourceProducts(nextSources);
      setTargetProducts(nextTargets);
      alert('已重新抓取商品圖片與樣式；可換商品新增樣式的庫存已預設 0。');
    } catch (error) {
      alert(`更新商品資料失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  const refreshRuleProductsFromJambo = async (rule) => {
    setIsLoading(true);
    try {
      const [nextSources, nextTargets] = await Promise.all([
        refreshProductListFromJambo(Array.isArray(rule.sourceProducts) ? rule.sourceProducts : [], { isTarget: false }),
        refreshProductListFromJambo(Array.isArray(rule.targetProducts) ? rule.targetProducts : [], { isTarget: true }),
      ]);
      await updateDoc(doc(db, 'tianlan_exchange_rules', rule.id), {
        sourceProducts: nextSources,
        targetProducts: nextTargets,
        sourceCommodityIds: nextSources.map(commodityIdOf),
        targetCommodityIds: nextTargets.map(commodityIdOf),
        updatedAt: new Date().toISOString(),
      });
      await loadRules();
      if (editingRuleId === rule.id) {
        setSourceProducts(nextSources);
        setTargetProducts(nextTargets);
      }
      alert('已更新這筆規則的商品圖片與樣式。');
    } catch (error) {
      alert(`更新規則商品資料失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  const resetRuleForm = () => {
    setSourceProducts([]);
    setTargetProducts([]);
    setAllowCancelRefund(false);
    setRuleTaskType('exchange');
    setEnableFormTask(false);
    setSelectedFormTemplateId('');
    setFormTemplateTitle('');
    setFormTemplateDescription('');
    setFormAllowSameAsPrevious(true);
    setFormAllowQuestionCollapse(true);
    setFormAllowAnswerGroupCountAdjust(true);
    setFormQuestions([
      { ...questionDefaults('text'), label: '請輸入姓名', minLength: 1, maxLength: 30 },
      { ...questionDefaults('calendar_date'), label: '請選擇生日' },
    ]);
    setEditingRuleId('');
    setDraggedQuestionId('');
    setDragOverQuestionId('');
    setQuestionOrderNotice('');
  };

  const openCreateRuleModal = () => {
    resetRuleForm();
    setIsRuleModalOpen(true);
  };

  const closeRuleModal = () => {
    setIsRuleModalOpen(false);
    resetRuleForm();
  };

  const changeRuleTaskType = (nextType) => {
    setRuleTaskType(nextType);
    if (nextType === 'form') {
      setEnableFormTask(true);
      setTargetProducts([]);
      setAllowCancelRefund(false);
    } else {
      setEnableFormTask(false);
    }
  };

  const startEditRule = (rule) => {
    const nextTaskType = rule.ruleTaskType || ((rule.targetProducts || []).length > 0 ? 'exchange' : 'form');
    setEditingRuleId(rule.id);
    setSourceProducts(Array.isArray(rule.sourceProducts)
      ? rule.sourceProducts.map((product) => compactCommodity({
        ...product,
        formResponsePolicy: product.formResponsePolicy || rule.formResponsePolicy || DEFAULT_SERVICE_RESPONSE_POLICY,
      }))
      : []);
    setRuleTaskType(nextTaskType);
    setTargetProducts(nextTaskType === 'exchange' && Array.isArray(rule.targetProducts) ? rule.targetProducts : []);
    setAllowCancelRefund(nextTaskType === 'exchange' && rule.allowCancelRefund === true);
    setEnableFormTask(nextTaskType === 'form');
    setSelectedFormTemplateId(rule.formTaskTemplateId || rule.questionTemplateId || '');
    setFormTemplateTitle('');
    setFormTemplateDescription('');
    setIsRuleModalOpen(true);
  };

  const updateFormQuestion = (id, patch) => {
    setFormQuestions((prev) => prev.map((question) => (question.id === id ? { ...question, ...patch } : question)));
  };

  const resetTemplateForm = () => {
    setEditingTemplateId('');
    setTemplateTitle('');
    setTemplateDescription('');
    setTemplateAllowSameAsPrevious(true);
    setTemplateAllowQuestionCollapse(true);
    setTemplateAllowAnswerGroupCountAdjust(true);
    setTemplateQuestions([
      { ...questionDefaults('text'), label: '請輸入姓名', minLength: 1, maxLength: 30 },
    ]);
    setDraggedQuestionId('');
    setDragOverQuestionId('');
    setQuestionOrderNotice('');
  };

  const openCreateTemplateModal = () => {
    resetTemplateForm();
    setIsTemplateModalOpen(true);
  };

  const closeTemplateModal = () => {
    setIsTemplateModalOpen(false);
    resetTemplateForm();
  };

  const startEditTemplate = (template) => {
    setEditingTemplateId(template.id);
    setTemplateTitle(template.title || template.formTitle || '');
    setTemplateDescription(template.description || '');
    setTemplateAllowSameAsPrevious(template.allowSameAsPrevious === true);
    setTemplateAllowQuestionCollapse(template.allowQuestionCollapse === true);
    setTemplateAllowAnswerGroupCountAdjust(template.allowAnswerGroupCountAdjust !== false);
    setTemplateQuestions(
      Array.isArray(template.questions) && template.questions.length > 0
        ? template.questions.map((question) => ({
          ...question,
          id: question.id || makeLocalId(),
          minLength: question.type === 'text' ? (question.minLength ?? 0) : '',
          maxLength: question.type === 'text' ? (question.maxLength ?? 200) : '',
          dateMode: question.type === 'date' ? (question.dateMode || 'date') : '',
          options: question.type === 'choice' ? (question.options || []) : [],
          branching: question.type === 'choice' && question.branching === true,
          condition: question.condition || null,
        }))
        : [{ ...questionDefaults('text'), label: '請輸入姓名', minLength: 1, maxLength: 30 }]
    );
    setIsTemplateModalOpen(true);
  };

  const updateTemplateQuestion = (id, patch) => {
    setTemplateQuestions((prev) => prev.map((question) => (question.id === id ? { ...question, ...patch } : question)));
  };

  const saveTemplate = async () => {
    const title = templateTitle.trim();
    const questions = cleanQuestionList(templateQuestions);
    if (!title) {
      alert('請輸入題目來源名稱');
      return;
    }
    if (questions.length === 0) {
      alert('請至少新增一個填寫項目');
      return;
    }
    setIsLoading(true);
    try {
      const now = new Date().toISOString();
      const existingTemplate = editingTemplateId
        ? serviceTemplates.find((template) => template.id === editingTemplateId)
        : null;
      const nextVersion = existingTemplate
        ? Math.max(1, Number(existingTemplate.version || 1) || 1) + 1
        : 1;
      const previousVersions = existingTemplate
        ? [...(Array.isArray(existingTemplate.previousVersions) ? existingTemplate.previousVersions : []), {
          version: Math.max(1, Number(existingTemplate.version || 1) || 1),
          title: existingTemplate.title || existingTemplate.formTitle || '',
          description: existingTemplate.description || '',
          allowSameAsPrevious: existingTemplate.allowSameAsPrevious === true,
          allowQuestionCollapse: existingTemplate.allowQuestionCollapse === true,
          allowAnswerGroupCountAdjust: existingTemplate.allowAnswerGroupCountAdjust !== false,
          questions: cleanQuestionList(
            Array.isArray(existingTemplate.questions) ? existingTemplate.questions : [],
          ),
          archivedAt: now,
        }].slice(-10)
        : [];
      const payload = withShopScope({
        title,
        description: templateDescription.trim(),
        allowSameAsPrevious: templateAllowSameAsPrevious,
        allowQuestionCollapse: templateAllowQuestionCollapse,
        allowAnswerGroupCountAdjust: templateAllowAnswerGroupCountAdjust,
        questions,
        version: nextVersion,
        previousVersions,
        enabled: true,
        updatedAt: now,
      }, currentShop);
      if (editingTemplateId) {
        await updateDoc(doc(db, 'tianlan_service_question_templates', editingTemplateId), payload);
      } else {
        await addDoc(collection(db, 'tianlan_service_question_templates'), {
          ...payload,
          createdAt: now,
        });
      }
      closeTemplateModal();
      await loadRules();
    } catch (error) {
      alert(`儲存題目來源失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  const removeTemplate = async (template) => {
    const title = template.title || template.formTitle || '未命名題目來源';
    const usedByRules = rules.filter((rule) => (
      rule.formTaskTemplateId === template.id || rule.questionTemplateId === template.id
    ));
    const warning = usedByRules.length > 0
      ? `\n\n注意：目前有 ${usedByRules.length} 筆商品規則正在使用這個題目來源，刪除後新任務會找不到題目。`
      : '';
    if (!window.confirm(`確定刪除「${title}」？${warning}`)) return;
    setIsLoading(true);
    try {
      await deleteDoc(doc(db, 'tianlan_service_question_templates', template.id));
      if (selectedFormTemplateId === template.id) {
        setSelectedFormTemplateId('');
      }
      await loadRules();
    } catch (error) {
      alert(`刪除題目來源失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  const applyQuestionMove = (questions, setQuestions, fromIndex, toIndex) => {
    const result = moveServiceQuestion(questions, fromIndex, toIndex);
    if (result.error) {
      setQuestionOrderNotice(result.error);
      return;
    }
    if (result.questions !== questions) {
      setQuestions(result.questions);
      setQuestionOrderNotice(`已將第 ${fromIndex + 1} 題移到第 ${toIndex + 1} 題。`);
    }
  };

  const removeBuilderQuestion = (setQuestions, question) => {
    setQuestions((previous) => {
      const dependents = previous.filter((item) => item.condition?.questionId === question.id);
      const remaining = previous
        .filter((item) => item.id !== question.id)
        .map((item) => (
          item.condition?.questionId === question.id
            ? { ...item, condition: null }
            : item
        ));
      setQuestionOrderNotice(
        dependents.length > 0
          ? `已移除「${question.label || '這個問題'}」，並將 ${dependents.length} 題改為一定顯示。`
          : `已移除「${question.label || '這個問題'}」。`,
      );
      return remaining;
    });
  };

  const renderQuestionBuilder = (questions, setQuestions, updateQuestion) => (
    <div className="tl-question-builder tl-template-question-builder">
      <div className="tl-question-order-toolbar">
        <span>
          <strong>問題順序</strong>
          <small>拖曳左側把手，或使用上移、下移。分流主問題會自動保持在子問題前面。</small>
        </span>
        <em>{questions.length} 題</em>
      </div>
      {questionOrderNotice && (
        <div className="tl-question-order-notice" role="status">
          <span>{questionOrderNotice}</span>
          <button type="button" onClick={() => setQuestionOrderNotice('')}>知道了</button>
        </div>
      )}
      {questions.map((question, index) => {
        const maxLengthUnlimited =
          question.type === 'text' &&
          (question.maxLength === '' ||
            question.maxLength === null ||
            question.maxLength === undefined ||
            Number(question.maxLength || 0) <= 0);
        const earlierChoiceQuestions = questions
          .slice(0, index)
          .filter((item) => item.type === 'choice');
        const conditionParent = earlierChoiceQuestions.find((item) => item.id === question.condition?.questionId);
        const conditionValue = question.condition?.values?.[0] || '';
        return (
          <div
            className={`tl-question-card ${dragOverQuestionId === question.id && draggedQuestionId !== question.id ? 'is-drag-over' : ''}`}
            key={question.id}
            onDragOver={(event) => {
              if (!draggedQuestionId || draggedQuestionId === question.id) return;
              event.preventDefault();
              setDragOverQuestionId(question.id);
            }}
            onDragLeave={() => {
              if (dragOverQuestionId === question.id) setDragOverQuestionId('');
            }}
            onDrop={(event) => {
              event.preventDefault();
              const fromIndex = questions.findIndex((item) => item.id === draggedQuestionId);
              if (fromIndex >= 0) applyQuestionMove(questions, setQuestions, fromIndex, index);
              setDraggedQuestionId('');
              setDragOverQuestionId('');
            }}
          >
            <div className="tl-question-card-head">
              <button
                aria-label={`拖曳排序第 ${index + 1} 題`}
                className="tl-question-drag-handle"
                draggable
                title="拖曳排序"
                type="button"
                onDragStart={(event) => {
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('text/plain', question.id);
                  setDraggedQuestionId(question.id);
                  setQuestionOrderNotice('');
                }}
                onDragEnd={() => {
                  setDraggedQuestionId('');
                  setDragOverQuestionId('');
                }}
              >
                <span aria-hidden="true">⋮⋮</span>
              </button>
              <span className="tl-question-order-number">{index + 1}</span>
              <span className="tl-question-card-kind">
                <strong>{questionTypeLabel(question)}</strong>
                <small>{conditionParent ? `分支：${conditionValue || '尚未指定'}` : '無條件顯示'}</small>
              </span>
              <span className="tl-question-order-actions">
                <button
                  aria-label={`上移第 ${index + 1} 題`}
                  disabled={index === 0}
                  type="button"
                  onClick={() => applyQuestionMove(questions, setQuestions, index, index - 1)}
                >
                  上移
                </button>
                <button
                  aria-label={`下移第 ${index + 1} 題`}
                  disabled={index === questions.length - 1}
                  type="button"
                  onClick={() => applyQuestionMove(questions, setQuestions, index, index + 1)}
                >
                  下移
                </button>
                <button
                  className="is-danger"
                  type="button"
                  onClick={() => removeBuilderQuestion(setQuestions, question)}
                >
                  移除
                </button>
              </span>
            </div>
            <label className="tl-labeled-field">
              <span>顯示文字</span>
              <input className="modern-input" value={question.label} onChange={(event) => updateQuestion(question.id, { label: event.target.value })} />
            </label>
            <label className="tl-check-option">
              <input type="checkbox" checked={question.required} onChange={(event) => updateQuestion(question.id, { required: event.target.checked })} />
              <span>必填</span>
            </label>
            {question.type === 'text' && (
              <div className="tl-form-row">
                <label className="tl-labeled-field">
                  <span>最少字數</span>
                  <input className="modern-input" type="number" min="0" value={question.minLength} onChange={(event) => updateQuestion(question.id, { minLength: event.target.value })} />
                </label>
                <label className="tl-labeled-field">
                  <span>最多字數</span>
                  <input
                    className="modern-input"
                    type="number"
                    min="0"
                    value={maxLengthUnlimited ? '' : question.maxLength}
                    disabled={maxLengthUnlimited}
                    placeholder={maxLengthUnlimited ? '不限制' : '0'}
                    onChange={(event) => updateQuestion(question.id, { maxLength: event.target.value })}
                  />
                  <div className="tl-check-option tl-inline-check-option">
                    <input
                      type="checkbox"
                      checked={maxLengthUnlimited}
                      onChange={(event) => updateQuestion(question.id, { maxLength: event.target.checked ? '' : 200 })}
                    />
                    <span>不限制最多字數</span>
                  </div>
                </label>
              </div>
            )}
            {question.type === 'date' && (
              <label className="tl-labeled-field">
                <span>日期格式</span>
                <select className="modern-input" value={question.dateMode || 'date'} onChange={(event) => updateQuestion(question.id, { dateMode: event.target.value })}>
                  <option value="date">只選日期</option>
                  <option value="datetime">日期與時間</option>
                </select>
              </label>
            )}
            {question.type === 'calendar_date' && (
              <div className="tl-question-module-note">
                客人會先選「國曆」或「農曆」，再選年月日。系統只保存客人原始選擇，不會自動換算。
              </div>
            )}
            {question.type === 'birth_time' && (
              <div className="tl-question-module-note">
                已內建不確定／不知道與十二時辰，客人可直接點選。
              </div>
            )}
            {question.type === 'choice' && (
              <>
                <label className="tl-labeled-field">
                  <span>選項，每行一個</span>
                  <textarea className="modern-input" value={(question.options || []).join('\n')} onChange={(event) => updateQuestion(question.id, { options: event.target.value.split('\n') })} />
                </label>
              </>
            )}
            <div className="tl-question-condition">
              <div className="tl-question-condition-head">
                <strong>顯示條件</strong>
                <span>{earlierChoiceQuestions.length > 0 ? '可依前面的選擇題分流' : '這題之前還沒有可分流的選擇題'}</span>
              </div>
              <div className="tl-form-row">
                <label className="tl-labeled-field">
                  <span>什麼情況顯示</span>
                  <select
                    className="modern-input"
                    value={question.condition?.questionId || ''}
                    disabled={earlierChoiceQuestions.length === 0}
                    onChange={(event) => {
                      const parent = earlierChoiceQuestions.find((item) => item.id === event.target.value);
                      updateQuestion(question.id, {
                        condition: parent
                          ? { questionId: parent.id, values: [(parent.options || [])[0] || ''] }
                          : null,
                      });
                    }}
                  >
                    <option value="">無條件，一定顯示</option>
                    {earlierChoiceQuestions.map((item, parentIndex) => (
                      <option key={item.id} value={item.id}>
                        當「{item.label || `選擇題 ${parentIndex + 1}`}」…
                      </option>
                    ))}
                  </select>
                </label>
                {conditionParent && (
                  <label className="tl-labeled-field">
                    <span>選到哪個選項</span>
                    <select
                      className="modern-input"
                      value={conditionValue}
                      onChange={(event) => updateQuestion(question.id, {
                        condition: { questionId: conditionParent.id, values: [event.target.value] },
                      })}
                    >
                      {(conditionParent.options || []).map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              {conditionParent && (
                <small>
                  當客人在「{conditionParent.label}」選擇「{conditionValue || '請選擇'}」時，才顯示這題。
                </small>
              )}
            </div>
          </div>
        );
      })}
      {questions.length === 0 && <div className="tl-empty-state">尚未新增填寫項目。</div>}
    </div>
  );

  const cleanQuestionList = (questions = []) =>
    questions
      .map((question, index) => ({
        ...Object.fromEntries(Object.entries(question).filter(([key]) => key !== 'reviewMode')),
        id: question.id || makeLocalId(),
        label: String(question.label || '').trim() || `問題 ${index + 1}`,
        options: question.type === 'choice'
          ? (question.options || []).map((option) => String(option || '').trim()).filter(Boolean)
          : [],
        branching: question.type === 'choice' && question.branching === true,
        condition: (() => {
          const parentId = String(question.condition?.questionId || '').trim();
          const parent = questions
            .slice(0, index)
            .find((item) => item.id === parentId && item.type === 'choice');
          const validOptions = new Set((parent?.options || []).map((value) => String(value || '').trim()));
          const values = (question.condition?.values || [])
            .map((value) => String(value || '').trim())
            .filter((value) => value && validOptions.has(value));
          return parent && values.length > 0
            ? { questionId: parentId, values }
            : null;
        })(),
      }))
      .filter((question) => question.label);

  const cleanFormQuestions = () => cleanQuestionList(formQuestions);

  const ruleTaskTypeOf = (rule = {}) =>
    rule.ruleTaskType || ((rule.targetProducts || []).length > 0 ? 'exchange' : 'form');

  const selectedStyleTextOf = (items = []) =>
    items.flatMap((item) => item.selectedStyles || []).filter(Boolean).join('、') || '全部樣式';

  const formRuleTemplateTextOf = (rule = {}) => {
    const templateId = rule.formTaskTemplateId || rule.questionTemplateId || '';
    const template = serviceTemplates.find((item) => item.id === templateId);
    const title = rule.formTaskTemplateTitle || template?.title || template?.formTitle || '';
    const questionLabels = (template?.questions || [])
      .map((question) => question.label)
      .filter(Boolean);
    if (title && questionLabels.length > 0) return `${title}（${questionLabels.join('、')}）`;
    if (title) return title;
    if (questionLabels.length > 0) return questionLabels.join('、');
    return templateId ? '已設定' : '未設定';
  };

  const responsePolicyTextOf = (value = {}) => {
    const policy = normalizeServiceResponsePolicy(value.formResponsePolicy || value);
    if (policy.mode === 'per_n') return `每 ${policy.quantityPerResponse} 件可填 1 份`;
    if (policy.mode === 'minimum_once') return `滿 ${policy.quantityPerResponse} 件需填 1 份`;
    if (policy.mode === 'minimum_then_per_n') {
      return `滿 ${policy.minimumQuantity} 件起，每件需填 1 份`;
    }
    return '每件可填 1 份';
  };

  const combinedFormTextOf = (rule = {}) => {
    const combinedProducts = (rule.sourceProducts || []).filter(isCombinedProduct);
    if (combinedProducts.length === 0) return '';
    return combinedProducts.map((item) => {
      const settings = normalizeCombinedFormSettings(item);
      if (item.formProductMode !== 'children') {
        return `${item.partNumber || item.name || item.commodityId}：整組填寫`;
      }
      const selected = settings.subCommodities.filter((child) => settings.selectedFormChildKeys.includes(child.key));
      return `${item.partNumber || item.name || item.commodityId}：拆分 ${selected.length} 項（${selected.map((child) => child.partNumber || child.name).join('、')}）`;
    }).join('；');
  };

  const ensureFormTemplate = async (forceFormTask = false) => {
    if (!forceFormTask && !enableFormTask) return { id: '', title: '' };
    if (selectedFormTemplateId) {
      const template = serviceTemplates.find((item) => item.id === selectedFormTemplateId);
      return { id: selectedFormTemplateId, title: template?.title || template?.formTitle || '售後表單' };
    }
    const questions = cleanFormQuestions();
    if (questions.length === 0) {
      throw new Error('請先設定表單題目，或選擇既有題目模板');
    }
    const mainProduct = sourceProducts[0] || {};
    const now = new Date().toISOString();
    const templateRef = doc(collection(db, 'tianlan_service_question_templates'));
    const title = formTemplateTitle.trim() || `${mainProduct.partNumber || mainProduct.name || '商品'}售後表單`;
    await setDoc(templateRef, withShopScope({
      title,
      description: formTemplateDescription.trim(),
      allowSameAsPrevious: formAllowSameAsPrevious,
      allowQuestionCollapse: formAllowQuestionCollapse,
      allowAnswerGroupCountAdjust: formAllowAnswerGroupCountAdjust,
      product: {
        id: commodityIdOf(mainProduct),
        commodityId: commodityIdOf(mainProduct),
        jamboCommodityId: commodityIdOf(mainProduct),
        partNumber: mainProduct.partNumber || mainProduct.itemNumber || '',
        itemNumber: mainProduct.partNumber || mainProduct.itemNumber || '',
        name: mainProduct.name || '',
        image: mainProduct.image || '',
        styles: styleLabelsOf(mainProduct),
        selectedStyles: mainProduct.selectedStyles || [],
      },
      questions,
      version: 1,
      previousVersions: [],
      enabled: true,
      createdAt: now,
      updatedAt: now,
    }, currentShop), { merge: true });
    return { id: templateRef.id, title };
  };

  const renderSelectedProducts = (items, setter, emptyText, options = {}) => {
    const isTarget = options.isTarget === true;
    const isFormSource = options.isFormSource === true;
    if (items.length === 0) return <div className="tl-row-sub">{emptyText}</div>;
    return (
      <div className="tl-style-product-stack">
        {items.map((item) => {
          const id = commodityIdOf(item);
          const styles = styleLabelsOf(item);
          const selectedStyles = Array.isArray(item.selectedStyles) ? item.selectedStyles : styles;
          const combinedSettings = normalizeCombinedFormSettings(item);
          const selectedFormChildKeys = Array.isArray(item.selectedFormChildKeys)
            ? item.selectedFormChildKeys
            : combinedSettings.selectedFormChildKeys;
          return (
            <div key={id} className="tl-style-product-card">
              <div className="tl-style-product-head">
                <ProductThumbnail product={item} size="selected" />
                <div className="tl-style-product-copy">
                  <div className="tl-row-title">{item.partNumber || id} {item.name}</div>
                  <div className="tl-row-sub">{styles.length > 0 ? `已選 ${selectedStyles.length}/${styles.length} 個樣式` : '商品資料沒有樣式，視為不限樣式'}</div>
                </div>
                <button type="button" className="modern-btn modern-btn-secondary" onClick={() => removeSelectedProduct(items, setter, id)}>移除</button>
              </div>
              {isFormSource && combinedSettings.combined && (
                <div className="tl-combined-form-config">
                  <div className="tl-combined-form-head">
                    <div>
                      <strong>組合商品如何填寫？</strong>
                      <span>這組包含 {combinedSettings.subCommodities.length} 項子商品</span>
                    </div>
                    <span className="tl-combined-badge">組合商品</span>
                  </div>
                  <div className="tl-combined-mode-options">
                    <label className={item.formProductMode !== 'children' ? 'is-selected' : ''}>
                      <input
                        type="radio"
                        name={`combined-mode-${id}`}
                        checked={item.formProductMode !== 'children'}
                        onChange={() => setFormProductMode(items, setter, id, 'combined')}
                      />
                      <span><strong>整組商品填寫</strong><small>以購買幾組計算可填份數</small></span>
                    </label>
                    <label className={item.formProductMode === 'children' ? 'is-selected' : ''}>
                      <input
                        type="radio"
                        name={`combined-mode-${id}`}
                        checked={item.formProductMode === 'children'}
                        onChange={() => setFormProductMode(items, setter, id, 'children')}
                      />
                      <span><strong>拆成子商品逐一填寫</strong><small>各子商品分開計算與顯示</small></span>
                    </label>
                  </div>
                  {item.formProductMode === 'children' && (
                    <div className="tl-combined-child-list">
                      <div className="tl-combined-child-list-head">
                        <strong>勾選需要建立表單的子商品</strong>
                        <span>已選 {selectedFormChildKeys.length} / {combinedSettings.subCommodities.length}</span>
                      </div>
                      {combinedSettings.subCommodities.map((child) => (
                        <label key={child.key} className={selectedFormChildKeys.includes(child.key) ? 'is-selected' : ''}>
                          <input
                            type="checkbox"
                            checked={selectedFormChildKeys.includes(child.key)}
                            onChange={() => toggleFormChild(items, setter, id, child.key)}
                          />
                          {child.image ? <img src={child.image} alt="" /> : <span className="tl-combined-child-placeholder">子</span>}
                          <span>
                            <strong>{child.partNumber || child.commodityId}　{child.name}</strong>
                            <small>{[child.style, child.subStyle].filter(Boolean).join(' / ') || '無樣式'}・每組 × {child.quantity}</small>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {isTarget && (
                <div className="tl-target-exchange-controls">
                  <div className="tl-row-actions tl-style-bulk-actions">
                    <button type="button" className="modern-btn modern-btn-secondary" onClick={() => updateAllManualStockForProduct(items, setter, id, 'unlimited')}>
                      全部設為無上限
                    </button>
                    <button type="button" className="modern-btn modern-btn-secondary" onClick={() => updateAllManualStockForProduct(items, setter, id, 'limited')}>
                      全部改回限量
                    </button>
                  </div>
                </div>
              )}
              {styles.length > 0 && (
                <div className="tl-style-chip-row">
                  {styles.map((style) => (
                    <button
                      type="button"
                      key={style}
                      className={`tl-style-chip ${selectedStyles.includes(style) ? 'is-selected' : ''}`}
                      onClick={() => toggleStyle(items, setter, id, style)}
                    >
                      <span>{style}</span>
                      {isTarget && <small>{manualStockText(item, style)}</small>}
                    </button>
                  ))}
                </div>
              )}
              {isTarget && (
                <div className="tl-manual-stock-grid">
                  {(styles.length > 0 ? styles : ['']).map((style) => {
                    const styleKey = normalizeStyleKey(style);
                    const stock = manualStockOf(item, style);
                    return (
                      <div className="tl-manual-stock-row" key={`${id}_${styleKey}`}>
                        <div>
                          <strong>{style || '無樣式'}</strong>
                          <span>設定給客人可選的數量，以這裡的數字為準。</span>
                        </div>
                        <label className="tl-check-option tl-stock-unlimited">
                          <input
                            type="checkbox"
                            checked={stock.mode === 'unlimited'}
                            onChange={(event) => updateManualStock(items, setter, id, style, {
                              mode: event.target.checked ? 'unlimited' : 'limited',
                            })}
                          />
                          <span>無上限</span>
                        </label>
                        <input
                          className="modern-input tl-stock-qty-input"
                          type="number"
                          min="0"
                          value={stock.quantity ?? ''}
                          disabled={stock.mode === 'unlimited'}
                          placeholder="0"
                          onChange={(event) => updateManualStock(items, setter, id, style, {
                            mode: stock.mode === 'unlimited' ? 'unlimited' : 'limited',
                            quantity: event.target.value,
                          })}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const saveRule = async () => {
    const isFormRule = ruleTaskType === 'form';
    if (sourceProducts.length === 0) {
      alert('請至少選一個任務商品');
      return;
    }
    if (!isFormRule && targetProducts.length === 0) {
      alert('請至少選一個可換商品');
      return;
    }
    const emptyCombinedSelection = isFormRule && sourceProducts.find((item) => (
      isCombinedProduct(item) &&
      item.formProductMode === 'children' &&
      (!Array.isArray(item.selectedFormChildKeys) || item.selectedFormChildKeys.length === 0)
    ));
    if (emptyCombinedSelection) {
      alert(`「${emptyCombinedSelection.name || emptyCombinedSelection.partNumber || '組合商品'}」請至少勾選一個要建立表單的子商品`);
      return;
    }
    setIsLoading(true);
    try {
      const formTemplate = await ensureFormTemplate(isFormRule);
      const normalizedSourceProducts = sourceProducts.map((product) => ({
        ...product,
        formResponsePolicy: isFormRule
          ? normalizeServiceResponsePolicy(product.formResponsePolicy || DEFAULT_SERVICE_RESPONSE_POLICY)
          : DEFAULT_SERVICE_RESPONSE_POLICY,
        formChildResponsePolicies: isFormRule
          ? Object.fromEntries(
            Object.entries(product.formChildResponsePolicies || {}).map(([key, policy]) => [
              key,
              normalizeServiceResponsePolicy(policy || product.formResponsePolicy || DEFAULT_SERVICE_RESPONSE_POLICY),
            ]),
          )
          : {},
      }));
      const normalizedPolicyTargets = formPolicyTargetsOf(normalizedSourceProducts);
      const payload = withShopScope({
        sourceCommodityIds: normalizedSourceProducts.map(commodityIdOf),
        targetCommodityIds: isFormRule ? [] : targetProducts.map(commodityIdOf),
        sourceProducts: normalizedSourceProducts,
        targetProducts: isFormRule ? [] : targetProducts,
        allowCancelRefund: !isFormRule && allowCancelRefund,
        formTaskTemplateId: formTemplate.id || '',
        questionTemplateId: formTemplate.id || '',
        formTaskTemplateTitle: formTemplate.title || '',
        formTaskEnabled: isFormRule && Boolean(formTemplate.id),
        formResponsePolicy: isFormRule
          ? normalizeServiceResponsePolicy(normalizedPolicyTargets[0]?.policy || DEFAULT_SERVICE_RESPONSE_POLICY)
          : DEFAULT_SERVICE_RESPONSE_POLICY,
        ruleTaskType,
        enabled: true,
        updatedAt: new Date().toISOString(),
      }, currentShop);
      if (editingRuleId) {
        await updateDoc(doc(db, 'tianlan_exchange_rules', editingRuleId), payload);
      } else {
        await addDoc(collection(db, 'tianlan_exchange_rules'), {
          ...payload,
          createdAt: new Date().toISOString(),
        });
      }
      resetRuleForm();
      setIsRuleModalOpen(false);
      await loadRules();
    } catch (error) {
      alert(`儲存規則失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  const toggleRule = async (rule) => {
    await updateDoc(doc(db, 'tianlan_exchange_rules', rule.id), {
      enabled: rule.enabled === false,
      updatedAt: new Date().toISOString(),
    });
    loadRules();
  };

  const removeRule = async (rule) => {
    if (!window.confirm('確定刪除此商品規則？')) return;
    await deleteDoc(doc(db, 'tianlan_exchange_rules', rule.id));
    loadRules();
  };

  const migrateOldProducts = async () => {
    if (!window.confirm('要把舊商品管理中的可換商品設定轉成新的商品規則嗎？')) return;
    setIsLoading(true);
    try {
      const migrate = adminCallable('tianlanMigrateProductsToExchangeRules');
      const result = await migrate(withShopScope({}, currentShop));
      alert(`轉換完成，新增 ${result.data?.created || 0} 筆規則`);
      await loadRules();
    } catch (error) {
      alert(`轉換失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  const selectedTemplate = serviceTemplates.find((template) => template.id === selectedFormTemplateId);
  const hasEmptyCombinedSelection = sourceProducts.some((item) => (
    isCombinedProduct(item) &&
    item.formProductMode === 'children' &&
    (!Array.isArray(item.selectedFormChildKeys) || item.selectedFormChildKeys.length === 0)
  ));
  const taskSetupIssues = [
    sourceProducts.length === 0 ? '尚未選任務商品' : '',
    ruleTaskType === 'exchange' && targetProducts.length === 0 ? '尚未選可換商品' : '',
    ruleTaskType === 'form' && hasEmptyCombinedSelection ? '拆分商品尚未勾選子商品' : '',
    ruleTaskType === 'form' && !selectedFormTemplateId && cleanFormQuestions().length === 0 ? '尚未設定題目來源' : '',
  ].filter(Boolean);
  const taskProductCount = ruleTaskType === 'form' ? formPolicyTargets.length : sourceProducts.length;
  const questionSourceLabel = selectedFormTemplateId
    ? (selectedTemplate?.title || selectedTemplate?.formTitle || '既有題目來源')
    : (formTemplateTitle.trim() || '使用這裡建立的新題目');
  const activeRuleCount = rules.filter((rule) => rule.enabled !== false).length;
  const formRuleCount = rules.filter((rule) => ruleTaskTypeOf(rule) === 'form').length;
  const exchangeRuleCount = rules.length - formRuleCount;

  return (
    <div className="modern-page">
      <div className="modern-page-header">
        <div className="modern-container">
          <div className="modern-page-title">
            <Link to="/exchange/tianlan" className="modern-back-btn">返回首頁</Link>
            <div>
              <h1>商品規則管理</h1>
              <div className="modern-page-subtitle">目前商店：{currentShop.storeName}（{currentShop.shopUserId}）</div>
            </div>
          </div>
        </div>
      </div>

      <div className="modern-container tl-admin-workspace">
        <section className="tl-rule-page-hero">
          <div className="tl-rule-page-hero-main">
            <div className="tl-admin-kicker">商品規則工作台</div>
            <h2>售後任務，一個畫面管理</h2>
            <p>先管理共用題目，再為商品建立表單或換貨任務。每張規則卡直接呈現實際商品、圖片與客人端設定。</p>
            <div className="tl-rule-page-actions">
              <button className="modern-btn modern-btn-primary" type="button" onClick={openCreateRuleModal}>建立商品任務</button>
              <button className="modern-btn modern-btn-secondary" type="button" onClick={openCreateTemplateModal} disabled={isLoading}>新增題目來源</button>
            </div>
          </div>
          <div className="tl-rule-page-stats" aria-label="商品規則總覽">
            <div><span>啟用中</span><strong>{activeRuleCount}</strong><small>共 {rules.length} 個任務</small></div>
            <div><span>表單任務</span><strong>{formRuleCount}</strong><small>收集客人資料</small></div>
            <div><span>換貨任務</span><strong>{exchangeRuleCount}</strong><small>管理可換庫存</small></div>
            <div><span>題目來源</span><strong>{serviceTemplates.length}</strong><small>可重複套用</small></div>
          </div>
        </section>

        <section className="tl-rule-page-section tl-template-library-section">
          <div className="tl-rule-page-section-head">
            <div>
              <span>01 · 共用設定</span>
              <h3>題目來源</h3>
              <p>先建立可重複套用的問題組合；修改後只影響之後新建立的任務。</p>
            </div>
            <button className="modern-btn modern-btn-secondary" type="button" onClick={openCreateTemplateModal} disabled={isLoading}>新增題目來源</button>
          </div>
          <div className="tl-template-source-grid">
            {serviceTemplates.map((template) => {
              const usedByRules = rules.filter((rule) => (
                rule.formTaskTemplateId === template.id || rule.questionTemplateId === template.id
              )).length;
              const title = template.title || template.formTitle || '未命名題目來源';
              const questions = Array.isArray(template.questions) ? template.questions : [];
              return (
                <article key={template.id} className="tl-template-source-card">
                  <div className="tl-template-source-main">
                    <div className="tl-template-card-status">
                      <span>題目來源</span>
                      <strong>{usedByRules > 0 ? `${usedByRules} 個任務使用中` : '尚未套用'}</strong>
                    </div>
                    <div className="tl-template-source-heading">
                      <div className="tl-row-title">{title}</div>
                      {!template.description && <span className="tl-template-warning">尚未填前台說明</span>}
                    </div>
                    <div className={`tl-row-sub ${template.description ? '' : 'is-empty-description'}`}>
                      {template.description || '建議補上用途與填寫原因，客人會更安心。'}
                    </div>
                    <div className="tl-template-source-metrics" aria-label="題目來源摘要">
                      <span><strong>{questions.length}</strong> 題</span>
                      <span><strong>{usedByRules}</strong> 個商品規則</span>
                      <span>版本 <strong>v{Math.max(1, Number(template.version || 1) || 1)}</strong></span>
                      <span className={template.allowSameAsPrevious === true ? 'is-on' : ''}>同上 {template.allowSameAsPrevious === true ? '開啟' : '關閉'}</span>
                      <span className={template.allowQuestionCollapse === true ? 'is-on' : ''}>折疊 {template.allowQuestionCollapse === true ? '開啟' : '關閉'}</span>
                      <span className={template.allowAnswerGroupCountAdjust !== false ? 'is-on' : ''}>套用份數 {template.allowAnswerGroupCountAdjust !== false ? '開啟' : '關閉'}</span>
                    </div>
                    <div className="tl-template-flow-label">客人會看到的問題流程</div>
                    <ServiceQuestionFlow questions={questions} compact />
                  </div>
                  <div className="tl-row-actions tl-template-card-actions">
                    <button className="modern-btn modern-btn-secondary" type="button" onClick={() => startEditTemplate(template)} disabled={isLoading}>
                      修改
                    </button>
                    <button className="modern-btn modern-btn-danger" type="button" onClick={() => removeTemplate(template)} disabled={isLoading}>
                      刪除
                    </button>
                  </div>
                </article>
              );
            })}
            {serviceTemplates.length === 0 && (
              <div className="tl-empty-state">
                尚未建立題目來源。可以先新增模板，或在建立表單任務時選「使用下方新題目」直接建立。
              </div>
            )}
          </div>
        </section>

        {isTemplateModalOpen && (
          <div className="tl-admin-modal-overlay tl-rule-wizard-overlay" role="presentation">
            <div className="tl-admin-modal tl-rule-wizard-modal" role="dialog" aria-modal="true" aria-label="題目來源管理">
              <div className="tl-admin-modal-head">
                <div>
                  <div className="tl-panel-title">{editingTemplateId ? '修改題目來源' : '新增題目來源'}</div>
                  <div className="tl-panel-desc">每次儲存會建立新版本；既有任務保留建立當時的題目，新建立的任務才使用最新版。</div>
                </div>
                <button className="modern-btn modern-btn-secondary" type="button" onClick={closeTemplateModal} disabled={isLoading}>
                  關閉
                </button>
              </div>
              <div className="tl-template-builder-grid">
                <div className="tl-form-stack tl-template-sidebar">
                  <div className="tl-builder-section-heading">
                    <strong>基本設定</strong>
                    <span>先命名並說明用途，再到右側安排問題。</span>
                  </div>
                  <label className="tl-labeled-field">
                    <span>題目來源名稱</span>
                    <input
                      className="modern-input"
                      value={templateTitle}
                      onChange={(event) => setTemplateTitle(event.target.value)}
                      placeholder="例如：姓名與生日資料"
                    />
                  </label>
                  <label className="tl-labeled-field">
                    <span>客人端說明</span>
                    <textarea
                      className="modern-input"
                      value={templateDescription}
                      onChange={(event) => setTemplateDescription(event.target.value)}
                      placeholder="讓客人知道為什麼需要填寫這份資料"
                    />
                  </label>
                  {!templateDescription.trim() && (
                    <div className="tl-description-suggestion">
                      <span>目前客人端沒有說明，建議用一句話交代用途。</span>
                      <button
                        type="button"
                        onClick={() => setTemplateDescription(suggestedTemplateDescription(templateTitle, templateQuestions))}
                      >
                        套用建議說明
                      </button>
                    </div>
                  )}
                  <div className="tl-template-behavior-options">
                    <label className="tl-check-option">
                      <input
                        type="checkbox"
                        checked={templateAllowSameAsPrevious}
                        onChange={(event) => setTemplateAllowSameAsPrevious(event.target.checked)}
                      />
                      <span><strong>允許「同上」</strong><small>同一 PA 有多個商品使用這份題目時，第二個開始可套用第一份答案。</small></span>
                    </label>
                    <label className="tl-check-option">
                      <input
                        type="checkbox"
                        checked={templateAllowQuestionCollapse}
                        onChange={(event) => setTemplateAllowQuestionCollapse(event.target.checked)}
                      />
                      <span><strong>允許折疊問題</strong><small>客人可收合已確認的答案組，減少長頁面滾動。</small></span>
                    </label>
                    <label className="tl-check-option">
                      <input
                        type="checkbox"
                        checked={templateAllowAnswerGroupCountAdjust}
                        onChange={(event) => setTemplateAllowAnswerGroupCountAdjust(event.target.checked)}
                      />
                      <span><strong>允許調整「套用份數」</strong><small>開啟時客人可決定每組答案套用幾份；關閉時由系統自動分配。</small></span>
                    </label>
                  </div>
                  <div className="tl-row-actions">
                    <button type="button" className="modern-btn modern-btn-secondary" onClick={() => setTemplateQuestions((prev) => [...prev, questionDefaults('text')])}>文字模組</button>
                    <button type="button" className="modern-btn modern-btn-secondary" onClick={() => setTemplateQuestions((prev) => [...prev, questionDefaults('calendar_date')])}>國／農曆生日</button>
                    <button type="button" className="modern-btn modern-btn-secondary" onClick={() => setTemplateQuestions((prev) => [...prev, questionDefaults('birth_time')])}>時辰模組</button>
                    <button type="button" className="modern-btn modern-btn-secondary" onClick={() => setTemplateQuestions((prev) => [...prev, questionDefaults('choice')])}>單選模組</button>
                    <button type="button" className="modern-btn modern-btn-primary" onClick={() => setTemplateQuestions((prev) => [...prev, branchingQuestionDefaults()])}>分流選擇</button>
                  </div>
                  <div className="tl-row-actions tl-form-actions">
                    <button className="modern-btn modern-btn-primary" type="button" onClick={saveTemplate} disabled={isLoading}>
                      {editingTemplateId ? '儲存修改' : '建立題目來源'}
                    </button>
                    <button className="modern-btn modern-btn-secondary" type="button" onClick={closeTemplateModal} disabled={isLoading}>
                      取消
                    </button>
                  </div>
                </div>
                {renderQuestionBuilder(templateQuestions, setTemplateQuestions, updateTemplateQuestion)}
                <aside className="tl-template-live-preview">
                  <div className="tl-builder-section-heading">
                    <strong>客人手機預覽</strong>
                    <span>分支與順序會即時更新；已建立的任務仍保留建立當下版本。</span>
                  </div>
                  <div className="tl-template-phone-frame">
                    <div className="tl-template-phone-head">
                      <span>售後服務表單</span>
                      <strong>{templateTitle.trim() || '尚未命名的題目來源'}</strong>
                      {templateDescription.trim() && <small>{templateDescription.trim()}</small>}
                    </div>
                    <ServiceQuestionFlow questions={templateQuestions} />
                  </div>
                </aside>
              </div>
            </div>
          </div>
        )}

        {isRuleModalOpen && (
          <div className="tl-admin-modal-overlay tl-rule-wizard-overlay" role="presentation">
            <div className="tl-admin-modal tl-rule-wizard-modal tl-task-editor-modal" role="dialog" aria-modal="true" aria-label={editingRuleId ? '編輯任務' : '建立任務'}>
              <div className="tl-admin-modal-head">
                <div>
                  <div className="tl-task-editor-kicker">商品規則工作台</div>
                  <div className="tl-panel-title">{editingRuleId ? '編輯任務' : '建立任務'}</div>
                  <div className="tl-panel-desc">依序完成商品、份數與題目設定；右側會即時整理最後結果。</div>
                </div>
                <div className="tl-task-editor-head-actions">
                  {editingRuleId && <span className="tl-pill">編輯中 <strong>{editingRuleId.slice(0, 6)}</strong></span>}
                  <button className="modern-btn modern-btn-secondary" type="button" onClick={closeRuleModal} disabled={isLoading}>關閉</button>
                </div>
              </div>

              <div className="tl-task-editor-layout">
                <main className="tl-task-editor-main">
                  <section className="tl-task-editor-section">
                    <div className="tl-task-editor-section-head">
                      <span>01</span>
                      <div><strong>任務類型與商品</strong><small>先決定客人要處理什麼，再選擇商品。</small></div>
                    </div>
                    <div className="tl-rule-wizard-steps">
                      <button type="button" className={`tl-rule-type-card ${ruleTaskType === 'exchange' ? 'is-selected' : ''}`} onClick={() => changeRuleTaskType('exchange')}>
                        <strong>換貨任務</strong><span>設定任務商品、可換商品與可選庫存。</span>
                      </button>
                      <button type="button" className={`tl-rule-type-card ${ruleTaskType === 'form' ? 'is-selected' : ''}`} onClick={() => changeRuleTaskType('form')}>
                        <strong>表單任務</strong><span>依商品數量產生客人要填寫的資料。</span>
                      </button>
                    </div>
                    <div className={`tl-workbench-grid tl-rule-wizard-picker-grid ${ruleTaskType === 'form' ? 'is-single' : ''}`}>
                      <JamboCommodityPicker shop={currentShop} title="選任務商品" pickedIds={pickedIds} onPick={(item) => setSourceProducts((prev) => addUnique(prev, item))} />
                      {ruleTaskType === 'exchange' && <JamboCommodityPicker shop={currentShop} title="選可換商品" pickedIds={pickedIds} onPick={(item) => setTargetProducts((prev) => addUnique(prev, item))} />}
                    </div>
                    <div className="tl-task-editor-toolbar">
                      <div><strong>已選任務商品</strong><small>組合商品可在下方決定整組填寫或拆分子商品。</small></div>
                      <button type="button" className="modern-btn modern-btn-secondary" onClick={refreshFormProductsFromJambo} disabled={isLoading || (sourceProducts.length === 0 && targetProducts.length === 0)}>更新商品資料</button>
                    </div>
                    {renderSelectedProducts(sourceProducts, setSourceProducts, '尚未選擇任務商品', { isFormSource: ruleTaskType === 'form' })}
                  </section>

                  <section className="tl-task-editor-section">
                    <div className="tl-task-editor-section-head">
                      <span>02</span>
                      <div>
                        <strong>{ruleTaskType === 'form' ? '各任務商品的填寫份數' : '可換商品與庫存'}</strong>
                        <small>{ruleTaskType === 'form' ? '拆分後每個子商品都能獨立設定，不再使用母組合商品代替。' : '設定客人可以選擇的商品與數量。'}</small>
                      </div>
                    </div>
                    {ruleTaskType === 'form' ? (
                      <div className="tl-product-response-policy-stack">
                        {formPolicyTargets.map((target) => (
                          <ProductResponsePolicyEditor
                            key={target.key}
                            product={target.product}
                            policy={target.policy}
                            kindLabel={target.kindLabel}
                            contextLabel={target.contextLabel}
                            onChange={(nextPolicy) => updateProductResponsePolicy(target.parentId, nextPolicy, target.childKey)}
                          />
                        ))}
                        {formPolicyTargets.length === 0 && <div className="tl-task-editor-empty">先在第 1 段選擇任務商品；若拆分組合商品，請至少勾選一個子商品。</div>}
                      </div>
                    ) : (
                      <div className="tl-task-editor-exchange-settings">
                        {renderSelectedProducts(targetProducts, setTargetProducts, '尚未選擇可換商品', { isTarget: true })}
                        <div className="tl-rule-note">可選庫存在客人送出時會先保留，避免同一份庫存被重複選走。</div>
                        <label className="tl-check-option"><input type="checkbox" checked={allowCancelRefund} onChange={(event) => setAllowCancelRefund(event.target.checked)} /><span>允許客人不換貨，申請退款</span></label>
                      </div>
                    )}
                  </section>

                  <section className="tl-task-editor-section">
                    <div className="tl-task-editor-section-head">
                      <span>03</span>
                      <div><strong>{ruleTaskType === 'form' ? '題目來源與客人端行為' : '確認任務內容'}</strong><small>{ruleTaskType === 'form' ? '套用既有題目，或直接在這裡建立新的題目來源。' : '確認商品與庫存後即可儲存。'}</small></div>
                    </div>
                    {ruleTaskType === 'form' ? (
                      <div className="tl-service-template-panel">
                        <label className="tl-labeled-field tl-task-template-select">
                          <span>題目來源</span>
                          <select className="modern-input" value={selectedFormTemplateId} onChange={(event) => setSelectedFormTemplateId(event.target.value)}>
                            <option value="">在這裡建立新題目</option>
                            {serviceTemplates.map((template) => <option key={template.id} value={template.id}>套用：{template.title || template.formTitle || '未命名模板'}</option>)}
                          </select>
                        </label>
                        {!selectedFormTemplateId && (
                          <div className="tl-template-builder-grid">
                            <div className="tl-form-stack">
                              <label className="tl-labeled-field"><span>表單名稱</span><input className="modern-input" value={formTemplateTitle} onChange={(event) => setFormTemplateTitle(event.target.value)} placeholder="例如：生日資料回填" /></label>
                              <label className="tl-labeled-field"><span>客人端說明</span><textarea className="modern-input" value={formTemplateDescription} onChange={(event) => setFormTemplateDescription(event.target.value)} placeholder="可留空" /></label>
                              <div className="tl-template-behavior-options">
                                <label className="tl-check-option"><input type="checkbox" checked={formAllowSameAsPrevious} onChange={(event) => setFormAllowSameAsPrevious(event.target.checked)} /><span><strong>允許「同上」</strong><small>第二個使用相同題目的商品可套用第一份答案。</small></span></label>
                                <label className="tl-check-option"><input type="checkbox" checked={formAllowQuestionCollapse} onChange={(event) => setFormAllowQuestionCollapse(event.target.checked)} /><span><strong>允許折疊問題</strong><small>客人可收合與展開答案組。</small></span></label>
                                <label className="tl-check-option"><input type="checkbox" checked={formAllowAnswerGroupCountAdjust} onChange={(event) => setFormAllowAnswerGroupCountAdjust(event.target.checked)} /><span><strong>允許調整「套用份數」</strong><small>開啟時客人可自行分配每組份數；關閉時由系統自動分配。</small></span></label>
                              </div>
                              <div className="tl-row-actions tl-task-question-actions">
                                <button type="button" className="modern-btn modern-btn-secondary" onClick={() => setFormQuestions((prev) => [...prev, questionDefaults('text')])}>文字</button>
                                <button type="button" className="modern-btn modern-btn-secondary" onClick={() => setFormQuestions((prev) => [...prev, questionDefaults('calendar_date')])}>國／農曆生日</button>
                                <button type="button" className="modern-btn modern-btn-secondary" onClick={() => setFormQuestions((prev) => [...prev, questionDefaults('birth_time')])}>時辰</button>
                                <button type="button" className="modern-btn modern-btn-secondary" onClick={() => setFormQuestions((prev) => [...prev, questionDefaults('choice')])}>單選</button>
                                <button type="button" className="modern-btn modern-btn-primary" onClick={() => setFormQuestions((prev) => [...prev, branchingQuestionDefaults()])}>分流選擇</button>
                              </div>
                            </div>
                            {renderQuestionBuilder(formQuestions, setFormQuestions, updateFormQuestion)}
                          </div>
                        )}
                      </div>
                    ) : <div className="tl-task-editor-ready-note">換貨任務會依同一張 PA 與商品樣式建立；儲存後即可在訂單處理頁使用。</div>}
                  </section>
                </main>

                <aside className="tl-task-editor-summary">
                  <div className="tl-task-editor-summary-head"><span>任務摘要</span><strong className={taskSetupIssues.length === 0 ? 'is-ready' : ''}>{taskSetupIssues.length === 0 ? '可以儲存' : `尚缺 ${taskSetupIssues.length} 項`}</strong></div>
                  <dl>
                    <div><dt>任務類型</dt><dd>{ruleTaskType === 'form' ? '表單任務' : '換貨任務'}</dd></div>
                    <div><dt>實際任務商品</dt><dd>{taskProductCount} 項</dd></div>
                    {ruleTaskType === 'exchange' && <div><dt>可換商品</dt><dd>{targetProducts.length} 項</dd></div>}
                    {ruleTaskType === 'form' && <div><dt>題目來源</dt><dd>{questionSourceLabel}</dd></div>}
                  </dl>
                  {ruleTaskType === 'form' && formPolicyTargets.length > 0 && (
                    <div className="tl-task-editor-summary-products">
                      <span>客人實際會看到的商品</span>
                      {formPolicyTargets.map((target) => (
                        <div key={target.key}>
                          <ProductThumbnail product={target.product} size="summary" />
                          <span><strong>{target.product.partNumber || target.product.commodityId}</strong><small>{target.product.name || '未命名商品'}</small><em>{responsePolicyTextOf(target.policy)}</em></span>
                        </div>
                      ))}
                    </div>
                  )}
                  {taskSetupIssues.length > 0 && <ul>{taskSetupIssues.map((issue) => <li key={issue}>{issue}</li>)}</ul>}
                </aside>
              </div>

              <div className="tl-row-actions tl-form-actions tl-rule-wizard-footer tl-task-editor-footer">
                <span>{taskSetupIssues.length === 0 ? '設定完成，儲存後會套用到新建立的任務。' : '完成上方缺少的設定後即可儲存。'}</span>
                <button className="modern-btn modern-btn-secondary" type="button" onClick={closeRuleModal} disabled={isLoading}>取消</button>
                <button className="modern-btn modern-btn-primary" onClick={saveRule} disabled={isLoading || taskSetupIssues.length > 0}>{editingRuleId ? '更新任務' : '儲存任務'}</button>
              </div>
            </div>
          </div>
        )}

        <section className="tl-rule-page-section tl-rule-library-section">
          <div className="tl-rule-page-section-head">
            <div>
              <span>02 · 商品任務</span>
              <h3>已建立的商品規則</h3>
              <p>每張卡片就是一個任務規則；直接查看商品、圖片、份數或庫存設定。</p>
            </div>
            <div className="tl-rule-page-section-actions">
              <button className="modern-btn modern-btn-secondary" onClick={migrateOldProducts} disabled={isLoading}>舊資料轉換</button>
              <button className="modern-btn modern-btn-primary" type="button" onClick={openCreateRuleModal}>建立商品任務</button>
            </div>
          </div>
          <div className="tl-rule-card-grid">
            {rules.map((rule) => {
              const isFormRule = ruleTaskTypeOf(rule) === 'form';
              const formTargets = isFormRule
                ? formPolicyTargetsOf(rule.sourceProducts || [], rule.formResponsePolicy)
                : [];
              const sourceProductsForDisplay = isFormRule
                ? formTargets.map((target) => ({ ...target.product, displayPolicy: target.policy }))
                : (rule.sourceProducts || []);
              return (
                <article key={rule.id} className={`tl-rule-task-card ${rule.enabled === false ? 'is-disabled' : ''}`}>
                  <header className="tl-rule-task-card-head">
                    <div>
                      <span className={`tl-rule-task-kind is-${isFormRule ? 'form' : 'exchange'}`}>{isFormRule ? '表單任務' : '換貨任務'}</span>
                      <span className={`tl-rule-task-status ${rule.enabled === false ? 'is-off' : ''}`}>{rule.enabled === false ? '已停用' : '啟用中'}</span>
                    </div>
                    <strong>{isFormRule ? '商品資料填寫' : '缺貨商品換貨'}</strong>
                  </header>

                  <div className={`tl-rule-product-flow ${isFormRule ? 'is-form' : ''}`}>
                    <div className="tl-rule-product-group">
                      <span>{isFormRule ? '客人實際填寫的商品' : '任務商品'}</span>
                      <div>
                        {sourceProductsForDisplay.map((product, index) => (
                          <ProductTile
                            key={`${commodityIdOf(product)}-${product.style || ''}-${index}`}
                            product={product}
                            meta={isFormRule ? responsePolicyTextOf(product.displayPolicy) : selectedStyleTextOf([product])}
                          />
                        ))}
                        {sourceProductsForDisplay.length === 0 && <div className="tl-rule-product-empty">尚未設定商品</div>}
                      </div>
                    </div>
                    {!isFormRule && (
                      <>
                        <div className="tl-rule-flow-arrow" aria-hidden="true">→</div>
                        <div className="tl-rule-product-group">
                          <span>可換商品</span>
                          <div>
                            {(rule.targetProducts || []).map((product, index) => (
                              <ProductTile
                                key={`${commodityIdOf(product)}-${index}`}
                                product={product}
                                meta={selectedStyleTextOf([product])}
                              />
                            ))}
                            {(rule.targetProducts || []).length === 0 && <div className="tl-rule-product-empty">尚未設定商品</div>}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <dl className="tl-rule-task-facts">
                    {isFormRule ? (
                      <>
                        <div><dt>題目來源</dt><dd>{formRuleTemplateTextOf(rule)}</dd></div>
                        <div><dt>計算方式</dt><dd>同一 PA＋同一商品＋同一樣式合計</dd></div>
                        {combinedFormTextOf(rule) && <div><dt>組合商品</dt><dd>{combinedFormTextOf(rule)}</dd></div>}
                      </>
                    ) : (
                      <>
                        <div><dt>可換庫存</dt><dd>{(rule.targetProducts || []).flatMap((item) => {
                          const styles = styleLabelsOf(item);
                          return (styles.length > 0 ? styles : ['']).map((style) => `${item.partNumber || item.commodityId} ${style || '無樣式'} ${manualStockText(item, style).replace('手動可選：', '')}`);
                        }).join('、') || '未設定'}</dd></div>
                        <div><dt>退款選項</dt><dd>{rule.allowCancelRefund === true ? '允許客人申請退款' : '不開放退款'}</dd></div>
                      </>
                    )}
                  </dl>

                  <footer className="tl-row-actions tl-rule-task-actions">
                    <button className="modern-btn modern-btn-secondary" onClick={() => startEditRule(rule)}>編輯</button>
                    <button className="modern-btn modern-btn-secondary" onClick={() => refreshRuleProductsFromJambo(rule)} disabled={isLoading}>同步商品資料</button>
                    <button className="modern-btn modern-btn-secondary" onClick={() => toggleRule(rule)}>{rule.enabled === false ? '啟用' : '停用'}</button>
                    <button className="modern-btn modern-btn-danger" onClick={() => removeRule(rule)}>刪除</button>
                  </footer>
                </article>
              );
            })}
            {rules.length === 0 && <div className="tl-empty-state">尚未建立規則。請點擊「建立任務」開始設定。</div>}
          </div>
        </section>
      </div>
    </div>
  );
}

export default TianlanJamboRuleManager;
